import 'swiper/css';

import {
  EIconButtonSize,
  EIconButtonVariant,
  GeneralCaretLeftIcon,
  GeneralCaretRightIcon,
  IconButton,
} from '@outdoorsyco/bonfire';
import kebabCase from 'lodash/kebabCase';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { ListingTile as GeneralPurposeListingTile } from '@/components/ui/GeneralPurposeListingTile';
import { getFlexibleDateRangeSelected } from '@/redux/selectors/queryParams';
import { IMapMarkerData } from '@/redux/selectors/search/map';
import { trackListingSelectedEvent } from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { trackEvent } from '@/services/track-event';

type TMapCarouselPopupProps = {
  marker?: IMapMarkerData;
  promoSlug?: string;
  isOnWishlist?: boolean;
};

export const MapCarouselPopup = ({ marker, promoSlug, isOnWishlist }: TMapCarouselPopupProps) => {
  const { isAboveDesktop } = useBreakpointContext();

  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);
  const listingTileUpdateEnabled = useExperimentIsEnabled(OptimizelyFlags.LISTING_TILE_UPDATE);

  // We would like to show the carousel only if experiment is enabled and the marker has multiple listings.
  const popupIsOpened = listingTileUpdateEnabled && (marker?.rentals.length || 0) > 1;

  const flexibleDateRangeSelected = useSelector(getFlexibleDateRangeSelected);

  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();
  const [isBeginning, setIsBeginning] = useState<boolean>(true);
  const [isEnd, setIsEnd] = useState<boolean>(true);

  useEffect(() => {
    if (!wrapperElement) return;

    const observer = new ResizeObserver(([entry]) => {
      setWrapperWidth(entry?.target.getBoundingClientRect().width || 0);
    });

    observer.observe(wrapperElement);

    return () => observer.disconnect();
  }, [wrapperElement]);

  useEffect(() => {
    if (!popupIsOpened) return;

    const toggleViewButton = document.getElementById('mobile-map-list-view-toggle');
    toggleViewButton?.style.setProperty('display', 'none');
    return () => toggleViewButton?.style.setProperty('display', '');
  }, [popupIsOpened]);

  // #mobile-map-list-view-toggle

  if (!popupIsOpened) return null;

  return (
    <div
      ref={setWrapperElement}
      className="relative w-full overflow-hidden bg-gray-50/25"
      onScroll={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}>
      <div style={{ width: `${wrapperWidth}px` }}>
        <Swiper
          slidesPerView="auto"
          onSwiper={swiper => {
            setSwiperInstance(swiper);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onActiveIndexChange={swiper => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onToEdge={swiper => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}>
          {marker?.rentals.map((rental, index) => {
            return (
              <SwiperSlide key={rental.linkUrl} className="!w-84">
                <GeneralPurposeListingTile
                  isTransparent={false}
                  listingTile={rental}
                  showFirstImageOnly={!isAboveDesktop}
                  hideFavoriteCount={isOnWishlist}
                  wishlistingEventData={{
                    eventSource: EListingSource.WISHLIST_MAP_MARKER,
                    index,
                    page: 1,
                  }}
                  showGuestFavoriteTag
                  showSuperhostBadge={superhostBadgeEnabled}
                  target={isAboveDesktop ? '_blank' : undefined}
                  onClick={() => {
                    trackEvent({
                      event: 'click/card/rental',
                      source: 'map-multi-marker',
                    });

                    trackListingSelectedEvent({
                      rental,
                      eventSource: promoSlug ? EListingSource.PROMO : EListingSource.MAP_PREVIEW,
                      listingPlacement: index + 1,
                      flexibleDateRange: flexibleDateRangeSelected,
                      promoSource: promoSlug || null,
                    });
                  }}
                  dataTestId={`${kebabCase(rental.title)}-card`}
                  className="mx-2 my-4 w-80"
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      <div className="flex justify-center px-2 pt-4 pb-6 gap-2">
        <IconButton
          size={EIconButtonSize.Small}
          icon={GeneralCaretLeftIcon}
          onClick={() => swiperInstance?.slidePrev()}
          variant={EIconButtonVariant.Primary}
          disabled={isBeginning}
        />

        <IconButton
          size={EIconButtonSize.Small}
          icon={GeneralCaretRightIcon}
          onClick={() => swiperInstance?.slideNext()}
          variant={EIconButtonVariant.Primary}
          disabled={isEnd}
        />
      </div>
    </div>
  );
};
